import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useCRMAuth } from '@/contexts/CRMAuthContext';
import CRMLayout from './CRMLayout';
import { toast } from 'sonner';
import {
  Plus, Repeat, Calendar, User, Building2, Loader2, Trash2,
  ToggleLeft, ToggleRight, Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function CRMRecurring() {
  const { token, isAdmin } = useCRMAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', client_id: '', assigned_to: '',
    frequency: 'weekly', day_of_week: 0, day_of_month: 1, priority: 'medium'
  });

  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchEmployees();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await fetch(`${API}/api/crm/recurring/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTasks(data.recurring_tasks || []);
      }
    } catch (err) {
      // Silent fail - tasks will show empty
    } 
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/crm/clients/?status=active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      // Silent fail - clients will show empty
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/api/crm/users?role=employee`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.users || []);
      }
    } catch (err) {
      // Silent fail - employees will show empty
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.client_id || !form.assigned_to) {
      toast.error('Fill required fields');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/recurring/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success('Recurring task created!');
        setShowModal(false);
        setForm({ title: '', description: '', client_id: '', assigned_to: '', frequency: 'weekly', day_of_week: 0, day_of_month: 1, priority: 'medium' });
        fetchTasks();
      }
    } catch (err) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      await fetch(`${API}/api/crm/recurring/${id}/toggle`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTasks();
    } catch (err) {
      // Silent fail - task toggle will be retried by user
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this recurring task?')) return;
    try {
      await fetch(`${API}/api/crm/recurring/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchTasks();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  return (
    <CRMLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Recurring Tasks</h1>
          <p className="text-slate-500">Auto-create tasks on schedule</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => setShowModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Create Recurring Task
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : tasks.length === 0 ? (
        <Card className="p-12 text-center">
          <Repeat className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No recurring tasks yet</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {tasks.map(task => (
            <Card key={task.id} className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${task.is_active ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                    <Repeat className={`w-5 h-5 ${task.is_active ? 'text-indigo-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{task.title}</h3>
                    <div className="flex flex-wrap gap-2 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{task.client_name}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{task.assigned_to_name}</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Badge className="bg-purple-100 text-purple-700">{task.frequency}</Badge>
                      <Badge className={task.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                        {task.is_active ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    {task.next_run && (
                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Next: {new Date(task.next_run).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleToggle(task.id)}>
                    {task.is_active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(task.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Create Recurring Task</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title *</label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Weekly report" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Client *</label>
                <Select value={form.client_id} onValueChange={(v) => setForm({...form, client_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.client_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Assign To *</label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({...form, assigned_to: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Frequency</label>
                <Select value={form.frequency} onValueChange={(v) => setForm({...form, frequency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQUENCY_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {(form.frequency === 'weekly' || form.frequency === 'biweekly') && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Day of Week</label>
                  <Select value={String(form.day_of_week)} onValueChange={(v) => setForm({...form, day_of_week: parseInt(v)})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {form.frequency === 'monthly' && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Day of Month</label>
                  <Input type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({...form, day_of_month: parseInt(e.target.value)})} />
                </div>
              )}
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Priority</label>
              <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CRMLayout>
  );
}
